import { createFileRoute } from '@tanstack/react-router';
import {
  getNotificationsControllerGetNotificationPreferencesSuspenseQueryOptions,
  useNotificationsControllerUpdateNotificationPreference,
} from '@/generated/api/notifications.ts';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { NotificationPreferencesDto } from '@/generated/api/pacApi.schemas.ts';
import { notifications } from '@mantine/notifications';
import NotificationPreferenceTable, {
  rowsFromNotificationPreferences,
} from '@/common/notifications/NotificationPreferenceTable.tsx';

export const Route = createFileRoute('/me/notifications/preferences')({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const queryOptions =
    getNotificationsControllerGetNotificationPreferencesSuspenseQueryOptions();
  const queryKey = queryOptions.queryKey;

  const preferences = useSuspenseQuery(queryOptions);

  const updatePreference =
    useNotificationsControllerUpdateNotificationPreference({
      mutation: {
        onMutate: async (newPreference) => {
          await queryClient.cancelQueries({
            queryKey: queryKey,
          });

          const snapshot: NotificationPreferencesDto | undefined =
            queryClient.getQueryData(queryKey);

          queryClient.setQueryData(
            queryKey,
            (previousValue: NotificationPreferencesDto | undefined) => {
              if (previousValue === undefined)
                throw new Error('previousValue is undefined');
              return {
                notificationPreferences: [
                  ...previousValue.notificationPreferences.map((p) => ({
                    ...p,
                    enabled:
                      p.notificationKey ===
                        newPreference.data.notificationKey &&
                      p.channel === newPreference.data.channel
                        ? newPreference.data.enabled
                        : p.enabled,
                  })),
                ],
              };
            },
          );

          return { snapshot };
        },
        onError: (_err, _newPreference, context) => {
          queryClient.setQueryData(
            queryKey,
            context?.snapshot ?? { notificationPreferences: [] },
          );
        },
        onSettled: () => {
          notifications.show({
            color: 'green',
            title: 'Updated Preference',
            message: 'Notification preferences updated',
          });
          queryClient.invalidateQueries({ queryKey });
        },
      },
    });

  return (
    <NotificationPreferenceTable
      rows={rowsFromNotificationPreferences(preferences.data)}
      disabled={updatePreference.isPending}
      onToggle={(preference, channel, enabled) =>
        updatePreference.mutate({
          data: {
            notificationKey: preference.key,
            channel,
            enabled,
          },
        })
      }
    />
  );
}
